/**
 * QrScannerModal — Sprint 5.0.4.
 *
 * Modal de leitura de QR code pela câmera do dispositivo. 100% no
 * navegador — NENHUMA imagem é enviada ao backend.
 *
 * Estratégia de decodificação:
 *   1. BarcodeDetector API (nativa, rápida) quando disponível
 *   2. Fallback jsQR (lib JS pura) decodificando frames do <canvas>
 *
 * Ciclo de vida:
 *   - Abre → pede getUserMedia (câmera traseira preferida)
 *   - Loop de detecção via requestAnimationFrame
 *   - Detectou QR → valida → mostra para confirmação
 *   - Confirma → onDetected(valor) → caller fecha o modal
 *   - Fecha → para TODAS as tracks do stream (sem vazar câmera)
 *
 * Tratamento de erro:
 *   - Permissão negada (NotAllowedError)
 *   - Sem câmera (NotFoundError)
 *   - Navegador sem getUserMedia
 *   - Navegador sem BarcodeDetector E sem jsQR → mensagem clara
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { t } from "../i18n/index.js";

// Sprint 5.0.4 — jsQR é importado DINAMICAMENTE (só quando o
// BarcodeDetector nativo não existe). Isso mantém o bundle principal
// leve: navegadores modernos com BarcodeDetector nunca baixam a lib.
let _jsQrModule = null;
async function loadJsQR() {
  if (_jsQrModule) return _jsQrModule;
  const mod = await import("jsqr");
  _jsQrModule = mod.default || mod;
  return _jsQrModule;
}

// Validação do QR — Sprint 5.0.4:
//   8-64 caracteres, apenas letras/números/hífen/underscore.
const QR_RE = /^[A-Za-z0-9_-]{8,64}$/;

export function isValidQrSerial(value) {
  return typeof value === "string" && QR_RE.test(value.trim());
}

// Estados do scanner (máquina de estados simples)
const ST = {
  STARTING: "starting",     // pedindo permissão / abrindo câmera
  SCANNING: "scanning",     // câmera ativa, procurando QR
  DETECTED: "detected",     // QR encontrado, aguardando confirmação
  ERROR: "error",           // erro de permissão / sem suporte / sem câmera
};

export default function QrScannerModal({ onDetected, onManual, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const rafRef = useRef(null);
  const detectorRef = useRef(null);
  const jsQrRef = useRef(null);   // Sprint 5.0.4 — jsQR carregado sob demanda
  const stoppedRef = useRef(false);

  const [state, setState] = useState(ST.STARTING);
  const [errMsg, setErrMsg] = useState(null);
  const [detected, setDetected] = useState(null);   // valor do QR detectado
  const [detectedInvalid, setDetectedInvalid] = useState(false);

  // ─── Para a câmera e cancela o loop ────────────────────────────────
  const stopEverything = useCallback(() => {
    stoppedRef.current = true;
    if (rafRef.current) {
      cancelAnimationFrame(rafRef.current);
      rafRef.current = null;
    }
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  // ─── Decodifica um frame ───────────────────────────────────────────
  const scanFrame = useCallback(async () => {
    if (stoppedRef.current) return;
    const video = videoRef.current;
    if (!video || video.readyState !== video.HAVE_ENOUGH_DATA) {
      rafRef.current = requestAnimationFrame(scanFrame);
      return;
    }

    let raw = null;

    // Caminho 1: BarcodeDetector nativo
    if (detectorRef.current) {
      try {
        const codes = await detectorRef.current.detect(video);
        if (codes && codes.length > 0) {
          raw = codes[0].rawValue;
        }
      } catch {
        // Alguns navegadores lançam em frames específicos — ignora e
        // continua o loop.
      }
    } else {
      // Caminho 2: jsQR sobre o canvas
      const jsQR = jsQrRef.current;
      const canvas = canvasRef.current;
      if (jsQR && canvas) {
        const w = video.videoWidth;
        const h = video.videoHeight;
        if (w > 0 && h > 0) {
          canvas.width = w;
          canvas.height = h;
          const ctx = canvas.getContext("2d", { willReadFrequently: true });
          ctx.drawImage(video, 0, 0, w, h);
          const imgData = ctx.getImageData(0, 0, w, h);
          const result = jsQR(imgData.data, w, h, {
            inversionAttempts: "dontInvert",
          });
          if (result && result.data) {
            raw = result.data;
          }
        }
      }
    }

    if (raw != null) {
      const value = String(raw).trim();
      // Para o loop, mostra para confirmação
      if (rafRef.current) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
      setDetected(value);
      setDetectedInvalid(!isValidQrSerial(value));
      setState(ST.DETECTED);
      return;
    }

    rafRef.current = requestAnimationFrame(scanFrame);
  }, []);

  // ─── Inicializa câmera + detector ──────────────────────────────────
  const startCamera = useCallback(async () => {
    stoppedRef.current = false;
    setState(ST.STARTING);
    setErrMsg(null);
    setDetected(null);
    setDetectedInvalid(false);

    // Sem getUserMedia → navegador antigo / contexto inseguro (http)
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setState(ST.ERROR);
      setErrMsg(t("qrScanner.errNoGetUserMedia"));
      return;
    }

    // Prepara o BarcodeDetector se existir e suportar qr_code
    detectorRef.current = null;
    jsQrRef.current = null;
    if ("BarcodeDetector" in window) {
      try {
        const formats = await window.BarcodeDetector.getSupportedFormats();
        if (formats.includes("qr_code")) {
          detectorRef.current = new window.BarcodeDetector({
            formats: ["qr_code"],
          });
        }
      } catch {
        detectorRef.current = null;
      }
    }
    // Sem BarcodeDetector → carrega jsQR dinamicamente (fallback).
    if (!detectorRef.current) {
      try {
        jsQrRef.current = await loadJsQR();
      } catch {
        setState(ST.ERROR);
        setErrMsg(t("qrScanner.errGeneric"));
        return;
      }
    }

    try {
      // Sprint 6.3 — constraints melhores para mobile: resolução
      // maior ajuda a ler QR pequenos; facingMode 'environment'
      // (não 'exact') p/ não falhar em aparelhos com 1 câmera.
      let stream;
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: {
            facingMode: { ideal: "environment" },
            width: { ideal: 1280 },
            height: { ideal: 720 },
          },
          audio: false,
        });
      } catch (inner) {
        // Fallback — alguns aparelhos rejeitam os hints de resolução.
        if (inner?.name === "OverconstrainedError" ||
            inner?.name === "ConstraintNotSatisfiedError") {
          stream = await navigator.mediaDevices.getUserMedia({
            video: { facingMode: "environment" },
            audio: false,
          });
        } else {
          throw inner;
        }
      }
      if (stoppedRef.current) {
        // Modal foi fechado enquanto pedíamos permissão
        stream.getTracks().forEach((tk) => tk.stop());
        return;
      }
      streamRef.current = stream;
      const video = videoRef.current;
      if (video) {
        video.srcObject = stream;
        video.setAttribute("playsinline", "true");
        video.setAttribute("muted", "true");
        await video.play();
      }
      setState(ST.SCANNING);
      rafRef.current = requestAnimationFrame(scanFrame);
    } catch (e) {
      setState(ST.ERROR);
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setErrMsg(t("qrScanner.errPermissionDenied"));
      } else if (name === "NotFoundError" || name === "DevicesNotFoundError") {
        setErrMsg(t("qrScanner.errNoCamera"));
      } else {
        setErrMsg(t("qrScanner.errGeneric"));
      }
    }
  }, [scanFrame]);

  // ─── Monta / desmonta ──────────────────────────────────────────────
  useEffect(() => {
    startCamera();
    return () => stopEverything();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ─── Handlers ──────────────────────────────────────────────────────
  const handleClose = () => {
    stopEverything();
    onClose();
  };

  const handleManual = () => {
    stopEverything();
    onManual();
  };

  const handleConfirm = () => {
    if (detected && !detectedInvalid) {
      stopEverything();
      onDetected(detected);
    }
  };

  const handleRescan = () => {
    setDetected(null);
    setDetectedInvalid(false);
    startCamera();
  };

  // ─── Render ────────────────────────────────────────────────────────
  return (
    <div
      className="fixed inset-0 z-[2300] bg-black/85 flex items-center justify-center p-4"
      onClick={(e) => e.target === e.currentTarget && handleClose()}
    >
      <div
        className="bg-noc-panel border border-noc-accent/30 rounded shadow-2xl w-full max-w-md flex flex-col"
        style={{ maxHeight: "92vh" }}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-noc-border">
          <h3 className="text-sm font-semibold text-noc-accent">
            📷 {t("qrScanner.title")}
          </h3>
          <button
            type="button"
            onClick={handleClose}
            className="text-noc-muted hover:text-noc-text text-xl leading-none"
          >
            ×
          </button>
        </div>

        {/* Corpo */}
        <div className="flex-1 overflow-y-auto p-4 space-y-3">
          {/* Vídeo + overlay (escondido em erro) */}
          {state !== ST.ERROR && (
            <div className="relative bg-black rounded overflow-hidden">
              <video
                ref={videoRef}
                className="w-full block"
                style={{ maxHeight: "50vh", objectFit: "cover" }}
                muted
              />
              {/* Moldura de mira */}
              {state === ST.SCANNING && (
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <div className="w-40 h-40 border-2 border-noc-accent/30 rounded-lg" />
                </div>
              )}
              {/* Canvas oculto — usado só pelo fallback jsQR */}
              <canvas ref={canvasRef} className="hidden" />
            </div>
          )}

          {/* Status: procurando */}
          {state === ST.STARTING && (
            <div className="text-xs text-noc-muted text-center">
              {t("qrScanner.starting")}
            </div>
          )}
          {state === ST.SCANNING && (
            <div className="text-xs text-noc-accent text-center animate-pulse">
              {t("qrScanner.searching")}
            </div>
          )}

          {/* QR detectado — confirmação */}
          {state === ST.DETECTED && (
            <div className="space-y-2">
              <div className="text-xs text-noc-muted">
                {t("qrScanner.detectedLabel")}
              </div>
              <div
                className={`font-mono text-sm p-2 rounded border break-all ${
                  detectedInvalid
                    ? "border-noc-err/30 bg-noc-err/10 text-noc-err"
                    : "border-noc-ok/30 bg-noc-ok/10 text-noc-ok"
                }`}
              >
                {detected}
              </div>
              {detectedInvalid && (
                <div className="text-[11px] text-noc-err">
                  ⚠ {t("qrScanner.invalidQr")}
                </div>
              )}
            </div>
          )}

          {/* Erro */}
          {state === ST.ERROR && (
            <div className="text-xs text-noc-err bg-noc-err/10 border border-noc-err/30 rounded p-3">
              {errMsg || t("qrScanner.errGeneric")}
            </div>
          )}
        </div>

        {/* Footer — botões dependem do estado */}
        <div className="px-4 py-3 border-t border-noc-border flex flex-wrap gap-2 justify-between">
          <button
            type="button"
            onClick={handleManual}
            className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
          >
            ⌨ {t("qrScanner.manualEntry")}
          </button>

          <div className="flex gap-2">
            {state === ST.DETECTED && (
              <>
                <button
                  type="button"
                  onClick={handleRescan}
                  className="px-3 py-1.5 text-xs rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10"
                >
                  🔄 {t("qrScanner.rescan")}
                </button>
                <button
                  type="button"
                  onClick={handleConfirm}
                  disabled={detectedInvalid}
                  className="px-3 py-1.5 text-xs rounded bg-noc-accent hover:bg-noc-glow text-noc-bg font-semibold disabled:opacity-40"
                >
                  ✓ {t("qrScanner.useThis")}
                </button>
              </>
            )}
            {state === ST.ERROR && (
              <button
                type="button"
                onClick={startCamera}
                className="px-3 py-1.5 text-xs rounded border border-noc-accent/30 text-noc-accent hover:bg-noc-accent/10"
              >
                🔄 {t("qrScanner.retry")}
              </button>
            )}
            {(state === ST.SCANNING || state === ST.STARTING) && (
              <button
                type="button"
                onClick={handleClose}
                className="px-3 py-1.5 text-xs rounded border border-noc-border text-noc-muted hover:text-noc-text"
              >
                ■ {t("qrScanner.stopCamera")}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
